import React, { ReactNode, createContext, useContext, useState, useEffect } from 'react';
import { RainbowKitProvider as RKProvider, darkTheme } from '@rainbow-me/rainbowkit';
import { WagmiProvider } from 'wagmi';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { useAccount, useBalance, useChainId, useDisconnect } from 'wagmi';
import { config } from '../config/wagmi';
import ethereumService from '../services/ethereumService';

// Import RainbowKit styles
import '@rainbow-me/rainbowkit/styles.css';

interface TokenBalance {
  symbol: string;
  balance: string;
  contractAddress: string;
  decimals: number;
}

interface VoiceTransaction {
  from: string;
  to: string;
  amount: string;
  timestamp: Date;
  type: string;
  hash: string;
}

interface Web3WalletContextType {
  isConnected: boolean;
  address: string | null;
  balance: string;
  tokenBalances: TokenBalance[];
  chainId: number | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  switchToBNBChain: () => Promise<void>;
  isConnecting: boolean;
  error: string | null;
  // Voice Token specific functions
  claimVoiceAirdrop: (claimCode: string, tier: number, signature: string) => Promise<{ success: boolean; hash?: string; error?: string }>;
  getVoiceTransactions: () => Promise<VoiceTransaction[]>;
  hasClaimedAirdrop: () => Promise<boolean>;
  getVoiceAirdropStats: () => Promise<any>;
  getVoiceGasFee: () => Promise<string>;
  voiceTransactions: VoiceTransaction[];
}

const Web3WalletContext = createContext<Web3WalletContextType | undefined>(undefined);

const queryClient = new QueryClient();

// Inner component that uses Wagmi hooks
const WalletContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { address, isConnected } = useAccount();
  const { data: balanceData } = useBalance({ address });
  const chainId = useChainId();
  const { disconnect } = useDisconnect();
  
  const [tokenBalances, setTokenBalances] = useState<TokenBalance[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voiceTransactions, setVoiceTransactions] = useState<VoiceTransaction[]>([]);

  // Format balance
  const balance = balanceData ? parseFloat(balanceData.formatted).toFixed(6) : '0';

  // Connect function (RainbowKit handles this through ConnectButton)
  const connect = async () => {
    setIsConnecting(true);
    setError(null);
    try {
      // RainbowKit handles connection through ConnectButton
      // This function is kept for compatibility
    } catch (err: any) {
      setError(err.message || 'Failed to connect wallet');
    } finally {
      setIsConnecting(false);
    }
  };

  // Switch to BNB Chain
  const switchToBNBChain = async () => {
    try {
      // RainbowKit handles chain switching automatically
      // This function is kept for compatibility
    } catch (err: any) {
      setError(err.message || 'Failed to switch network');
    }
  };

  // Voice Token specific functions
  const claimVoiceAirdrop = async (claimCode: string, tier: number, signature: string) => {
    if (!address) {
      return { success: false, error: 'Wallet not connected' };
    }
    
    try {
      return await ethereumService.claimVoiceAirdrop(claimCode, tier, signature);
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const getVoiceTransactions = async (): Promise<VoiceTransaction[]> => {
    if (!address) return [];
    
    try {
      return await ethereumService.getVoiceTransactionHistory(address);
    } catch (error) {
      console.error('Error fetching Voice transactions:', error);
      return [];
    }
  };

  const hasClaimedAirdrop = async (): Promise<boolean> => {
    if (!address) return false;
    
    try {
      return await ethereumService.hasClaimedAirdrop(address);
    } catch (error) {
      console.error('Error checking airdrop claim status:', error);
      return false;
    }
  };

  const getVoiceAirdropStats = async () => {
    try {
      return await ethereumService.getVoiceAirdropStats();
    } catch (error) {
      console.error('Error fetching airdrop stats:', error);
      return null;
    }
  };

  const getVoiceGasFee = async (): Promise<string> => {
    try {
      return await ethereumService.getVoiceGasFee();
    } catch (error) {
      console.error('Error fetching gas fee:', error);
      return '0.001';
    }
  };

  // Update token balances when address changes
  useEffect(() => {
    if (address && isConnected) {
      updateTokenBalances();
      loadVoiceTransactions();
    }
  }, [address, isConnected]);

  const updateTokenBalances = async () => {
    if (!address) return;
    
    try {
      // Get VOICE token balance
      const voiceTokenAddress = import.meta.env.VITE_VOICE_TOKEN_ADDRESS;
      if (voiceTokenAddress && voiceTokenAddress !== '0x0000000000000000000000000000000000000000') {
        const voiceBalance = await ethereumService.getTokenBalance(address, voiceTokenAddress);
        setTokenBalances([{
          symbol: 'VOICE',
          balance: voiceBalance.toString(),
          contractAddress: voiceTokenAddress,
          decimals: 18
        }]);
      }
    } catch (error) {
      console.error('Error updating token balances:', error);
    }
  };

  const loadVoiceTransactions = async () => {
    if (!address) return;
    
    try {
      const transactions = await getVoiceTransactions();
      setVoiceTransactions(transactions);
    } catch (error) {
      console.error('Error loading Voice transactions:', error);
    }
  };

  const contextValue: Web3WalletContextType = {
    isConnected,
    address: address || null,
    balance,
    tokenBalances,
    chainId,
    connect,
    disconnect,
    switchToBNBChain,
    isConnecting,
    error,
    claimVoiceAirdrop,
    getVoiceTransactions,
    hasClaimedAirdrop,
    getVoiceAirdropStats,
    getVoiceGasFee,
    voiceTransactions,
  };

  return (
    <Web3WalletContext.Provider value={contextValue}>
      {children}
    </Web3WalletContext.Provider>
  );
};

// Main RainbowKit Provider
export const RainbowKitProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <RKProvider
          theme={darkTheme({
            accentColor: '#e5aa17',
            accentColorForeground: 'white',
            borderRadius: 'medium',
            fontStack: 'system',
            overlayBlur: 'small',
          })}
          modalSize="compact"
          locale="en-US"
        >
          <WalletContextProvider>
            {children}
          </WalletContextProvider>
        </RKProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};

// Hook to use the wallet context
export const useWeb3Wallet = () => {
  const context = useContext(Web3WalletContext);
  if (context === undefined) {
    throw new Error('useWeb3Wallet must be used within a RainbowKitProvider');
  }
  return context;
};

// Export for backward compatibility
export const Web3WalletProvider = RainbowKitProvider;